<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { IOrder } from '../../interfaces/IOrder';
  import DisplayValue from '../DisplayValue.vue';
  import { Globals } from '../../modules/Globals';
  import {
    OrderPaymentStatus,
    OrderPaymentStatusColor,
    OrderStatus,
    OrderStatusColor,
    OrderType,
  } from '../../enums/OrderEnums';
  import { ICurrency } from '../../interfaces/ICurrency';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import OrdersService from '../../api/services/orders.service';
  import { IOrderItem } from '../../interfaces/IOrderItem';
  import { PaymentMethod } from '../../enums/PaymentMethod';
  import { bitCount, getDisplayOptions } from '../../utils';
  import { IOption } from '../../interfaces/IOption';
  import { IOrderItemOption } from '../../interfaces/IOrderItemOption';
  import { OptionType } from '../../enums/OptionType';
  import TwoColumnScrollableModal from './base/TwoColumnScrollableModal.vue';
  import { PrinterType } from '../../modules/PrintService';
  import OrderItems from './order/OrderItems.vue';
  import ModalPay from './pay/ModalPay.vue';
  import InputModal from './InputModal.vue';
  import { maybePromptCoversAfterAccept } from '@/composition/covers-accept-prompt';
  import { usePrinting } from '@/composition/use-printing';
  import { useInvoiceAttempt } from '@/composition/use-invoice-attempt';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import InvoicingItemService from '@/api/services/invoicing/invoicing-item.service';
  import CreateInvoiceModalArg from '@/components/modals/order/CreateInvoiceModalArg.vue';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { PaymentType } from '@/enums/PaymentEnums';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import {
    getOnlineAcceptCajaGuardError,
    getPaidOrderCancelDeclineGuardError,
    isOrderInvoiceable,
    orderInvoiceableTotal,
  } from '@/utils/orders.utils';
  import { roundPrice } from '@/utils';
  import { downloadInvoicePdf as generateInvoicePdf } from '@/composition/use-invoice-pdf';
  import { canShareNativePdf } from '@/lib/invoicing/deliver-pdf';
  import InvoicePdfPreviewModal from '@/components/modals/InvoicePdfPreviewModal.vue';

  @Component({
    components: { OrderItems, TwoColumnScrollableModal, DisplayValue },
  })
  export default class OrderModal extends Vue {
    @Prop() order: IOrder;
    @Prop() printEnabled: boolean;
    @Prop({ default: false }) hideActions: boolean;
    @Prop({ default: true }) shouldReload: boolean;
    ordersService = new OrdersService();
    currency: ICurrency = null;
    apiPath = Globals.apiPath;
    uploadsPath = Globals.uploadsPath;
    loading = false;
    orderItems: IOrderItem[] = null;
    editingOrderItems: IOrderItem[] = null;
    paymentMethods = Globals.shortPaymentMethods;
    printing = usePrinting();
    invoiceAttempt = useInvoiceAttempt();
    invoiceLoading = false;
    cajaMovements: ICajaMovement[] = null;
    shortPaymentMethosMap = Globals.shortPaymentMethosMap;
    restaurantStore = useRestaurantStore();

    created() {
      this.currency = Globals.getCurrency(this.order.currency);
    }

    mounted() {
      this.init();
    }

    beforeDestroy() {
      this.clearListeners();
    }

    setUpListeners() {
      this.$socket.client.addEventListener(
        `r:${this.order.restaurantId}:${this.order.branchId}:orderUpdate`,
        this.onOrderUpdated
      );
    }

    clearListeners() {
      this.$socket.client.removeEventListener(
        `r:${this.order.restaurantId}:${this.order.branchId}:orderUpdate`,
        this.reloadOrder
      );
    }

    onOrderUpdated(orderId: number) {
      if (orderId === this.order.id) this.reloadOrder(true).catch(() => {});
    }

    async init() {
      this.setUpListeners();
      await this.reloadOrder();
    }

    async reloadOrder(forced = false) {
      await Promise.all([
        this.reloadOrderItems(),
        ...[
          this.shouldReload || forced
            ? OrdersService.getById(this.order.id).then((updatedOrder) => {
                for (const key in updatedOrder) {
                  if (updatedOrder[key] !== undefined)
                    this.order[key] = updatedOrder[key];
                }
              })
            : null,
        ],
        this.reloadCajaMovements().catch(() => {}),
      ]);
    }

    async reloadCajaMovements() {
      if (this.order.totalPaid <= 0 || !this.branch.settingUseCajas) return;
      try {
        this.cajaMovements = await OrdersService.getCajaMovements(
          this.order.id
        );
        this.cajaMovements = this.cajaMovements.filter(
          (m) => !m.reimbursed && m.amount > 0
        );
      } catch (e) {
        console.error(e);
        vueData.serverError(
          e,
          'Ocurrió un error al obtener los movimientos de caja'
        );
      }
    }

    async reloadOrderItems() {
      this.orderItems = null;
      this.orderItems = await this.ordersService.getOrderItems(this.order.id);
    }

    get restaurant() {
      return vueData.restaurant;
    }

    get effectiveTier(): number {
      return getEffectiveTier(this.restaurant);
    }

    get branch() {
      return vueData.branch;
    }

    get paymentMethod() {
      return this.paymentMethods.find(
        (value) => value.id === this.order.paymentMethod
      );
    }

    get fullAddress() {
      if (this.order.type !== OrderType.Delivery || !this.order.userAddress2)
        return this.order.userAddress;

      const end = ` ${this.order.userAddress2}`;
      return this.order.userAddress.endsWith(end)
        ? this.order.userAddress
        : this.order.userAddress + end;
    }

    get multiplePaymentMethods() {
      return bitCount(this.activePaymentMethods) > 1;
    }

    get activePaymentMethods() {
      switch (this.order.type) {
        case OrderType.Seat:
          return this.branch.paymentMethodsA;
        case OrderType.Away:
          return this.branch.paymentMethodsB;
        case OrderType.Delivery:
          return this.branch.paymentMethodsC;
      }
    }

    print(printerType: PrinterType, order: IOrder) {
      this.$parent.$emit('print', printerType, order);
    }

    get invoiceInProgress() {
      return this.invoiceAttempt.isInvoiceLive(this.order.id);
    }

    get isInvoiceable() {
      return isOrderInvoiceable(this.order);
    }

    async createInvoice() {
      if (this.order.invoicingItemId || this.invoiceInProgress) return;
      let invoicingItem = null;

      // Invoice transparency: surface the comps (invitación/empleado)
      // exclusion before emitting.
      const subtotal = roundPrice(Number(this.order.total ?? 0));
      const invoiceTotal = orderInvoiceableTotal(this.order);
      const summary = {
        ordersCount: 1,
        subtotal,
        excludedTotal: roundPrice(subtotal - invoiceTotal),
        invoiceTotal,
      };

      try {
        const invoiceData: {
          invoiceType: number;
          idType: number;
          id: string;
        } = await vueData.showDynamicDialog(
          this.$modal,
          CreateInvoiceModalArg,
          { summary },
          'create-invoice'
        );

        if (!invoiceData) return;

        this.invoiceLoading = true;
        // Keeps settling in the background if the user closes the modal;
        // this continuation still runs on terminal (toast + print below).
        invoicingItem = await this.invoiceAttempt.createInvoice(
          this.order.id,
          invoiceData
        );

        this.order.invoicingItemId = invoicingItem.id;
        vueData.addAlert('Factura emitida', AlertType.Success);
      } catch (e) {
        console.error(e);
        vueData.serverError(e, 'Ocurrió un error al crear la factura');
      } finally {
        this.invoiceLoading = false;
      }

      if (
        this.order.invoicingItemId &&
        invoicingItem &&
        this.printing.isPrinterConnected(PrinterType.Ticket)
      ) {
        this.printing.printInvoice(invoicingItem).catch((e) => {
          console.error(e);
          vueData.serverError(e, 'Ocurrió un error al imprimir la factura');
        });
      }
    }

    async printInvoice() {
      if (!this.order.invoicingItemId) return;

      try {
        this.invoiceLoading = true;
        const invoicingItem = await InvoicingItemService.getOne(
          this.order.invoicingItemId
        );

        await this.printing.printInvoice(invoicingItem);
      } catch (e) {
        console.error(e);
        vueData.serverError(e, 'Ocurrió un error al imprimir la factura');
      } finally {
        this.invoiceLoading = false;
      }
    }

    get invoiceMenuItems() {
      const items = [
        {
          title: 'Imprimir factura',
          icon: 'mdi-printer',
          click: () => this.printInvoice(),
        },
      ];

      // Legacy shells (old APKs without Share/Filesystem) can neither share
      // the PDF nor download blobs from the WebView, so hide the action.
      if (!Globals.isCapacitor || canShareNativePdf()) {
        items.push({
          title: 'Descargar PDF',
          icon: 'mdi-file-download-outline',
          click: () => this.downloadInvoicePdf(),
        });
      }

      return items;
    }

    async downloadInvoicePdf() {
      if (!this.order.invoicingItemId) return;

      try {
        this.invoiceLoading = true;
        const invoicingItem = await InvoicingItemService.getOne(
          this.order.invoicingItemId
        );

        await generateInvoicePdf({
          invoicingItem,
          orderItems: this.orderItems,
          order: this.order,
          branch: vueData.branch,
          // Order-scoped currency (set in created()); vueData's restaurant
          // context may differ when opened from the payments dashboard.
          currencySymbol: this.currency.symbol,
          openPreview: (blob, filename) => {
            this.$modal.show(
              InvoicePdfPreviewModal,
              { blob, filename },
              {
                name: 'invoice-pdf-preview',
                adaptive: true,
                height: 'auto',
                scrollable: true,
                width: 900,
                maxWidth: window.innerWidth * 0.95,
              }
            );
          },
        });
      } catch (e) {
        console.error(e);
        vueData.serverError(e, 'No se pudo generar la factura en PDF');
      } finally {
        this.invoiceLoading = false;
      }
    }

    async decline() {
      try {
        const didUpdate = await this.setStatus(OrderStatus.Declined);
        if (didUpdate) {
          this.$emit('close');
        }
      } catch (e) {}
    }

    async confirm() {
      try {
        const didUpdate = await this.setStatus(OrderStatus.Progress);
        if (didUpdate) {
          // Fire-and-forget covers confirmation when this accept opened the
          // table.
          void maybePromptCoversAfterAccept(this.$modal, this.order);
        }
      } catch (e) {}
    }

    async setStatus(
      status: OrderStatus,
      errorMessage = null
    ): Promise<boolean> {
      const paidGuardError = getPaidOrderCancelDeclineGuardError({
        order: this.order,
        status,
      });

      if (paidGuardError) {
        vueData.addAlert(paidGuardError, AlertType.Error);
        return false;
      }

      if (status === OrderStatus.Progress) {
        const guardError = getOnlineAcceptCajaGuardError({
          order: this.order,
          caja: this.restaurantStore.caja,
          effectiveTier: this.effectiveTier,
          branchSettingUseCajas: this.branch?.settingUseCajas,
        });

        if (guardError) {
          vueData.addAlert(guardError, AlertType.Error);
          return false;
        }
      }

      this.loading = true;
      try {
        const res = await OrdersService.setStatus(
          this.order.id,
          status,
          this.restaurantStore.caja?.id
        );
        this.order.status = res.status;
        this.$parent.$emit('statusChanged', res.status);
        return true;
      } catch (e) {
        console.error(e);
        vueData.serverError(
          e,
          errorMessage ?? this.$tc('errors.statusChangeFailed')
        );
        throw e;
      } finally {
        this.loading = false;
      }
    }

    async doPayment() {
      this.$modal.show(
        ModalPay,
        {
          //order: this.order,
          type: PaymentType.Order,
          unpaidAmount: this.unpaidAmount,
          entityIds: [this.order.id],
        },
        {
          name: 'order-pay',
          //classes: 'dialog--height-limit',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          /*closed: async () => {
            this.reloadCajaMovements().catch(() => {});
          },*/
        }
      );
    }

    async showSetDiscountModal() {
      this.$modal.show(
        InputModal,
        {
          title: 'Descuento',
          value: this.order.discount,
          type: 'number',
          message: 'Ingrese el porcentaje de descuento',
          hint: 'Ejemplo: 10',
          placeholder: 'Descuento',
          prefix: '%',
          rules: [
            (v) =>
              (v >= 0 && v <= 100) || 'El descuento debe ser entre 0 y 100',
          ],
        },
        {
          name: 'set-discount',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          submit: async (value) => {
            try {
              await OrdersService.setDiscount({
                orderId: this.order.id,
                discount: +value,
              });
              this.$modal.hide('set-discount');
            } catch (e) {
              console.error(e);
              vueData.serverError(
                e,
                'Ocurrió un error al aplicar el descuento'
              );
            }
          },
        }
      );
    }

    async setProgress() {
      await this.setStatus(OrderStatus.Progress);
    }

    async setDelivering() {
      await this.setStatus(OrderStatus.Delivering);
    }

    async setFinished() {
      await this.setStatus(OrderStatus.Finished);
    }

    async setReadyTA() {
      await this.setStatus(OrderStatus.ReadyToTake);
    }

    async setCanceled() {
      await this.setStatus(
        OrderStatus.Canceled,
        this.$tc('order.errorCanceling')
      );
    }

    getStatusColor(status: OrderStatus) {
      return OrderStatusColor[status] ?? 'grey';
    }

    getStatusName(status: OrderStatus) {
      return this.$tc(`order.status.${status}`);
    }

    getPaymentStatusColor(status: OrderPaymentStatus) {
      if (status === 'await' && this.order.totalPaid > 0) {
        return 'orange';
      }
      return OrderPaymentStatusColor[status] ?? 'grey';
    }

    get unpaidAmount() {
      return this.order.total - this.order.totalPaid;
    }

    getPaymentStatusName(order: IOrder) {
      const status = order.paymentStatus;

      if (status === 'await' && order.totalPaid > 0) {
        return `Faltan ${this.currency.symbol}${this.unpaidAmount}`;
      }

      switch (status) {
        case 'await':
          return this.$tc('order.paymentStatus.notPaid');
        case 'paid':
          return this.$tc('order.paymentStatus.paid');
        case 'declined':
          return this.$tc('order.paymentStatus.declined');
        case 'reimbursed':
          return this.$tc('order.paymentStatus.reimbursed');
      }
    }

    cancel() {
      this.$emit('close');
    }
  }
</script>

<template>
  <two-column-scrollable-modal class="order-modal-container">
    <template #title>
      <h3>{{ $t('order') }}</h3>
    </template>
    <template #left>
      <div class="d-flex flex-row align-center">
        <v-spacer />
        <template v-if="printEnabled">
          <v-btn
            @click="print(0, order)"
            v-tooltip.bottom="'Imprimir ticket no fiscal'"
            small
            text
            rounded
            color="#aaa"
            class="px-1"
          >
            <v-icon>mdi-printer</v-icon>
            <v-icon small>mdi-cash-usd</v-icon>
          </v-btn>
          <v-btn
            @click="print(1, order)"
            v-tooltip.bottom="'Imprimir comanda'"
            small
            text
            rounded
            color="#aaa"
            class="ma-1 px-1"
          >
            <v-icon>mdi-printer</v-icon>
            <v-icon small>mdi-chef-hat</v-icon>
          </v-btn>
          <v-btn
            @click="print(2, order)"
            v-tooltip.bottom="'Imprimir etiqueta'"
            small
            text
            rounded
            color="#aaa"
            class="ma-1 px-1"
          >
            <v-icon>mdi-printer</v-icon>
            <v-icon small>mdi-tag</v-icon>
          </v-btn>
        </template>
      </div>
      <div class="order-info">
        <div class="order-data">
          <div>
            <template v-if="order.type === 'seat'"
              ><span
                class="order-title"
                v-html="
                  $t(`order.description.short`, {
                    tableName: order.table.name,
                  })
                "
              ></span
            ></template>
            <template v-else-if="order.type === 'away'"
              ><span
                class="order-title"
                v-html="
                  $t(`order.descriptionTakeAway.short`, {
                    number: order.humanId,
                  })
                "
              ></span
            ></template>
            <template v-else>
              <span
                class="order-title"
                v-html="
                  $t(`order.descriptionDelivery.short`, {
                    address: fullAddress,
                  })
                "
              ></span>
              <v-btn
                v-if="order.deliveryLat"
                class="font-weight-regular px-1 py-3 mt-1 mb-1"
                color="info"
                x-small
                depressed
                target="_blank"
                rounded
                plain
                :href="`https://www.google.com/maps/search/?api=1&query=${order.deliveryLat},${order.deliveryLng}`"
              >
                <v-icon class="mr-1">mdi-map</v-icon>
              </v-btn>
            </template>
          </div>
          <div v-if="order.type === 'delivery' && order.userAddressComments">
            <v-icon small>mdi-map-marker</v-icon>
            <span>
              {{ order.userAddressComments }}
            </span>
          </div>
          <div>
            <div
              v-if="
                order.type === 'delivery' &&
                paymentMethod &&
                (multiplePaymentMethods || order.paymentMethod > 1)
              "
            >
              <display-value title="Medio de pago" space-between>{{
                paymentMethod.title
              }}</display-value>
            </div>
          </div>
          <template>
            <div class="tw-flex tw-space-x-2 tw-items-center">
              <span
                >Para <b>{{ order.userName }}</b></span
              >
              <template v-if="order.userWhatsapp || order.userTel">
                <div class="tw-flex tw-items-center">
                  <v-btn
                    target="_blank"
                    :href="`https://wa.me/${
                      order.userWhatsapp ||
                      order.userTel.replace('+', '').replace('-', '')
                    }`"
                    color="green"
                    class="only-icon"
                    dark
                    text
                    rounded
                    small
                    icon
                  >
                    <v-icon small>mdi-whatsapp</v-icon>
                  </v-btn>
                  <v-btn
                    color="green"
                    target="_blank"
                    :href="`tel:${order.userWhatsapp || order.userTel}`"
                    dark
                    text
                    rounded
                    small
                  >
                    <v-icon class="pr-1" small>mdi-phone</v-icon>
                    {{ order.userWhatsapp || order.userTel }}
                  </v-btn>
                </div>
              </template>
            </div>
          </template>
          <!-- Custom fields -->
          <v-row v-if="order.orderFields && order.orderFields.length">
            <v-col
              cols="12"
              class="py-0"
              v-for="orderField of order.orderFields"
              :key="orderField.fieldId"
              v-if="orderField.value"
            >
              <display-value
                :title="orderField.field.title"
                :space-between="true"
                >{{ orderField.value }}</display-value
              >
            </v-col>
          </v-row>
          <!-- Cubiertos -->
          <display-value
            v-if="
              order.type === 'seat' &&
              (order.covers != null || order.coversSuggested != null)
            "
            :title="$t('order.covers')"
            :space-between="true"
          >
            <span v-if="order.covers != null" class="font-weight-bold">{{
              order.covers
            }}</span>
            <span v-else class="font-weight-regular"
              >{{ order.coversSuggested }} ({{
                $t('order.coversSuggested')
              }})</span
            >
          </display-value>
          <!-- Costos -->
          <div class="costs">
            <display-value
              v-if="order.subtotal !== order.total"
              title="Subtotal"
              :space-between="true"
            >
              <span class="font-weight-bold"
                >{{ currency.symbol }}{{ order.subtotal }}</span
              >
            </display-value>
            <display-value
              v-if="order.discountAmount"
              title="Descuento"
              :space-between="true"
            >
              <span class="font-weight-bold"
                >{{ order.discount }}% / {{ currency.symbol
                }}{{ order.discountAmount }}</span
              >
            </display-value>
            <template v-if="order.deliveryCost !== null || order.taxMode === 3">
              <display-value
                v-if="order.deliveryCost !== null"
                title="Costo de envío"
                :space-between="true"
              >
                <span
                  v-if="order.deliveryCost === 0"
                  class="font-weight-regular"
                  >Gratis</span
                >
                <span v-else class="font-weight-bold"
                  >{{ currency.symbol }}{{ order.deliveryCost }}</span
                >
              </display-value>
              <display-value
                v-if="order.taxMode === 3"
                :title="`${order.taxName} (${order.tax}%)`"
                :space-between="true"
              >
                <span class="font-weight-bold"
                  >{{ currency.symbol }}{{ order.taxAmount }}</span
                >
              </display-value>
            </template>
            <display-value
              :title="$t('total')"
              :space-between="true"
              :note="order.roundDifference ? 'Incluye redondeo' : undefined"
            >
              <div class="tw-flex tw-items-center tw-space-x-2">
                <span class="font-weight-bold"
                  >{{ currency.symbol }}{{ order.total }}</span
                >
                <v-btn
                  color="info"
                  x-small
                  @click="showSetDiscountModal"
                  elevation="0"
                  rounded
                  >Descuento</v-btn
                >
              </div>
            </display-value>
          </div>
          <!-- Comentarios -->
          <div v-if="order.comment">
            <div>
              <v-icon small class="mr-1">mdi-comment</v-icon>
              <b>{{ $t('order.comments') }}</b>
            </div>
            <p style="white-space: pre-line; margin-bottom: 0 !important">
              {{ order.comment }}
            </p>
          </div>
          <div class="order-meta">
            <div class="tw-flex tw-items-center">
              <v-icon small class="mr-1">mdi-account</v-icon>
              <span>{{ order.user ? order.user.name : $t('anonymous') }}</span>
            </div>
            <v-chip :color="getStatusColor(order.status)" small label dark>{{
              getStatusName(order.status)
            }}</v-chip>
            <v-chip
              :color="getPaymentStatusColor(order.paymentStatus)"
              small
              label
              dark
              >{{ getPaymentStatusName(order) }}</v-chip
            >
          </div>
        </div>
        <v-row v-if="cajaMovements?.length">
          <v-col cols="12" class="py-0">
            <div class="movements-data">
              <h4>Pagos</h4>
              <div class="movements">
                <div
                  v-for="(movement, index) in cajaMovements"
                  :key="movement.id"
                  class="movement"
                >
                  <span class="index">{{ cajaMovements.length - index }}.</span>
                  <span class="description">
                    <span class="movement-title"
                      >{{ shortPaymentMethosMap[movement.paymentMethod]
                      }}{{
                        movement.comment ? ` - ${movement.comment}` : ''
                      }}</span
                    >
                    <timeago
                      class="timeago"
                      :datetime="movement.createdAt"
                      :auto-update="60"
                    ></timeago>
                  </span>
                  <div class="tw-flex-grow"></div>
                  <span class="amount"
                    >{{ currency.symbol }}{{ movement.amount }}</span
                  >
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="!hideActions">
          <v-col cols="12" class="py-0">
            <h4>Acciones</h4>
            <div class="order-actions">
              <template v-if="order.status === 'received'">
                <v-btn
                  color="success"
                  small
                  @click="confirm"
                  :disabled="loading"
                >
                  <v-icon>mdi-check</v-icon>
                  <span>{{ $t('confirm') }}</span>
                </v-btn>
                <v-btn
                  color="error"
                  small
                  @click="decline"
                  :disabled="loading || order.paymentMethod > 2"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                  <span>{{ $t('decline') }}</span>
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  v-if="order.paymentStatus !== 'paid'"
                  color="success"
                  small
                  @click="doPayment"
                  :disabled="loading"
                >
                  <v-icon>mdi-cash</v-icon>
                  <span>Pagar</span>
                  <span>{{ currency.symbol }}{{ unpaidAmount }}</span>
                </v-btn>
                <template v-else>
                  <v-btn
                    v-if="!order.invoicingItemId && isInvoiceable"
                    color="info"
                    small
                    @click="createInvoice"
                    :disabled="
                      loading ||
                      restaurant.country !== 'AR' ||
                      effectiveTier < 2 ||
                      invoiceInProgress
                    "
                    :loading="invoiceLoading"
                  >
                    <v-icon>{{
                      invoiceInProgress ? 'mdi-timer-sand' : 'mdi-file-document'
                    }}</v-icon>
                    <span>{{
                      invoiceInProgress ? 'Factura en proceso' : 'Facturar'
                    }}</span>
                  </v-btn>
                  <div
                    v-else-if="order.invoicingItemId"
                    class="button-group invoice-split"
                  >
                    <v-btn
                      color="info"
                      small
                      class="invoice-split-main"
                      :disabled="loading || restaurant.country !== 'AR'"
                      :loading="invoiceLoading"
                      @click="printInvoice"
                    >
                      <v-icon>mdi-printer</v-icon>
                      <span>Imprimir Factura</span>
                    </v-btn>
                    <v-menu offset-y top left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          color="info"
                          small
                          class="invoice-split-toggle"
                          :disabled="
                            loading ||
                            invoiceLoading ||
                            restaurant.country !== 'AR'
                          "
                          aria-label="Más opciones de factura"
                          v-on="on"
                          v-bind="attrs"
                        >
                          <v-icon>mdi-chevron-down</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item
                          v-for="item in invoiceMenuItems"
                          :key="item.title"
                          @click="item.click"
                        >
                          <v-list-item-icon>
                            <v-icon>{{ item.icon }}</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title>{{
                              item.title
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>
                </template>
                <!-- Archived orders belong to a closed table: their status is
                     frozen (the server rejects the regression anyway). -->
                <v-btn
                  v-if="order.status === 'finished' && !order.archived"
                  color="primary"
                  small
                  @click="setProgress"
                  :disabled="loading || order.status !== 'finished'"
                >
                  <v-icon>mdi-arrow-left</v-icon>
                  <span>Regresar a {{ $t('order.status.progress') }}</span>
                </v-btn>
                <div class="button-group" v-if="order.type === 'seat'">
                  <v-btn
                    color="primary"
                    small
                    @click="setFinished"
                    :disabled="loading || order.status !== 'progress'"
                  >
                    <v-icon>mdi-check</v-icon>
                    <span>{{ $t('order.status.finished') }}</span>
                  </v-btn>
                </div>
                <div class="button-group" v-else-if="order.type === 'away'">
                  <v-btn
                    color="info"
                    small
                    @click="setReadyTA"
                    :disabled="loading || order.status !== 'progress'"
                  >
                    <v-icon>mdi-package-variant</v-icon>
                    <span>{{ $t('order.status.readyta') }}</span>
                  </v-btn>
                  <v-btn
                    color="primary"
                    small
                    @click="setFinished"
                    :disabled="loading || order.status === 'finished'"
                  >
                    <v-icon>mdi-check</v-icon>
                    <span>{{ $t('order.status.delivered') }}</span>
                  </v-btn>
                </div>
                <div class="button-group" v-else>
                  <v-btn
                    color="info"
                    small
                    @click="setDelivering"
                    :disabled="loading || order.status !== 'progress'"
                  >
                    <v-icon>mdi-truck-delivery-outline</v-icon>
                    <span>{{ $t('order.status.delivering') }}</span>
                  </v-btn>
                  <v-btn
                    color="primary"
                    small
                    @click="setFinished"
                    :disabled="loading || order.status === 'finished'"
                  >
                    <v-icon>mdi-check</v-icon>
                    <span>{{ $t('order.status.delivered') }}</span>
                  </v-btn>
                </div>
              </template>
              <template v-if="order.status !== 'received'">
                <v-btn
                  color="red"
                  small
                  dark
                  @click="setCanceled"
                  :disabled="loading || order.status !== 'progress'"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                  <span>{{ $t('order.cancel') }}</span>
                </v-btn>
              </template>
            </div>
          </v-col>
        </v-row>
      </div>
    </template>
    <template #right>
      <h3>{{ $t('order.items') }}</h3>
      <template v-if="!orderItems">
        <v-skeleton-loader type="list-item-avatar-two-line" tile />
        <v-skeleton-loader type="list-item-avatar-two-line" tile />
      </template>
      <template v-else>
        <OrderItems
          :order="order"
          :currency="currency"
          :items="orderItems"
          @update="reloadOrder"
        />
      </template>
    </template>
    <template #action-bar>
      <div class="d-flex flex-row align-center justify-end">
        <v-btn text @click="cancel">{{ $t('close') }}</v-btn>
      </div>
    </template>
  </two-column-scrollable-modal>
  <!--<v-card class="order-modal-container">
    <v-card-title class="pb-0">{{ $t('order') }}</v-card-title>
    <v-card-text>
      <v-row justify="center">
        <v-col cols="12" md="auto">

        </v-col>
        <v-col cols="12" md="auto" class="flex-fill">

        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn text @click="cancel">{{ $t('close') }}</v-btn>
    </v-card-actions>
  </v-card>-->
</template>

<style lang="scss" scoped>
  .order-info {
    @apply tw-space-y-4;

    .display-value {
      margin: 0;
    }

    .order-data {
      @apply tw-bg-gray-100 tw-rounded-xl tw-px-3 tw-py-2 tw-space-y-1;

      .order-title {
        @apply tw-font-bold;
      }

      .order-meta {
        @apply tw-flex tw-justify-end tw-space-x-2;
      }

      .costs {
        @apply tw-border-t-2 tw-border-b-2 tw-border-dashed tw-border-gray-300 tw-py-2;
      }
    }

    .movements-data {
      @apply tw-bg-gray-100 tw-rounded-xl tw-px-3 tw-py-2 tw-space-y-1;

      h4 {
        @apply tw-font-bold;
      }

      .movements {
        @apply tw-space-y-2;
      }

      .movement {
        @apply tw-flex tw-justify-between tw-text-sm tw-space-x-2;

        .index {
          @apply tw-text-gray-500 tw-text-xs;
        }

        .description {
          @apply tw-flex tw-flex-col;

          .timeago {
            @apply tw-text-xs tw-text-gray-500;
          }
        }
      }
    }

    .order-actions {
      @apply tw-flex tw-space-x-2 tw-items-center tw-pb-2 tw-justify-start;

      & > * {
        @apply tw-flex-shrink tw-flex-grow-0;
      }

      button {
        /* make it square */
        @apply tw-w-20 tw-h-20 tw-block #{!important};
        @apply tw-flex tw-justify-center tw-items-center tw-whitespace-pre-wrap tw-break-words tw-font-normal;
        text-transform: none;
        font-size: 0.7rem;

        ::v-deep {
          span {
            @apply tw-flex-col tw-items-center tw-justify-center;
          }
        }
      }

      .button-group {
        @apply tw-flex tw-items-stretch tw-justify-center;

        button {
          &:not(:last-child) {
            @apply tw-rounded-r-none;
          }

          &:not(:first-child) {
            @apply tw-rounded-l-none;
          }
        }
      }

      .invoice-split {
        /* v-menu may wrap its activator, so the .button-group sibling
           radius rules are not reliable here: set radii explicitly. */
        .invoice-split-main {
          @apply tw-rounded-r-none #{!important};
        }

        .invoice-split-toggle {
          /* Narrow chevron-only toggle next to the square main button */
          @apply tw-w-8 tw-px-0 tw-rounded-l-none #{!important};
          min-width: 32px !important;
          /* Divider so the group visibly reads as two buttons */
          border-left: 1px solid rgba(255, 255, 255, 0.4) !important;
        }
      }
    }
  }
</style>
